// Account Contacts tab — Accounts-Contacts-01.
//
// Renders the EXTERNAL contacts at the employer Account. These are
// distinct from the internal Client team (broker employees) on the
// Overview tab. Surfaces the standard CRUD: list, add modal, edit
// modal, set-primary, delete (soft).
//
// Employer Portal invitations are intentionally NOT implemented in
// this pass. The "Invite to portal" affordance is omitted so the
// surface stays honest while the portal flow is on the backlog.

import { useMemo, useState } from "react";

import {
  Badge, Button, Table, TBody, THead, Tr, Th as DsTh, Td as DsTd,
} from "@/design-system";
import { ConfirmDialog } from "@/components/ConfirmDialog";

import { AccountContactFormModal } from "./AccountContactFormModal";
import {
  useAccountContactsQuery,
  useCreateAccountContactMutation,
  useDeleteAccountContactMutation,
  useSetPrimaryAccountContactMutation,
  useUpdateAccountContactMutation,
} from "./hooks";
import type {
  AccountContact, AccountContactInput, AccountContactPatch,
} from "./types";


export interface AccountContactsTabProps {
  accountId: string;
}


export function AccountContactsTab({
  accountId,
}: AccountContactsTabProps) {
  const listQuery = useAccountContactsQuery(accountId);
  const createMutation = useCreateAccountContactMutation(accountId);
  const updateMutation = useUpdateAccountContactMutation(accountId);
  const deleteMutation = useDeleteAccountContactMutation(accountId);
  const setPrimaryMutation = useSetPrimaryAccountContactMutation(accountId);

  const [modalState, setModalState] = useState<
    | { mode: "create" }
    | { mode: "edit"; contact: AccountContact }
    | null
  >(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AccountContact | null>(null);

  const rows: AccountContact[] = useMemo(
    () => listQuery.data?.rows ?? [],
    [listQuery.data],
  );

  const hasExistingPrimary = rows.some((r) => r.is_primary);

  const onAdd = () => {
    setModalError(null);
    setModalState({ mode: "create" });
  };

  const onEdit = (c: AccountContact) => {
    setModalError(null);
    setModalState({ mode: "edit", contact: c });
  };

  const onSubmit = (values: AccountContactInput) => {
    setModalError(null);
    if (modalState?.mode === "edit") {
      const patch: AccountContactPatch = values;
      updateMutation.mutate(
        { contact_id: modalState.contact.contact_id, values: patch },
        {
          onSuccess: () => setModalState(null),
          onError: (err) => setModalError(describeError(err)),
        },
      );
      return;
    }
    createMutation.mutate(values, {
      onSuccess: () => setModalState(null),
      onError: (err) => setModalError(describeError(err)),
    });
  };

  const onSetPrimary = (c: AccountContact) => {
    if (c.is_primary) return;
    setPrimaryMutation.mutate(c.contact_id);
  };

  const onConfirmRemove = () => {
    if (!removing) return;
    deleteMutation.mutate(removing.contact_id, {
      onSettled: () => setRemoving(null),
    });
  };

  const pending =
    updateMutation.isPending
    || setPrimaryMutation.isPending
    || deleteMutation.isPending;

  return (
    <section
      data-testid="account-contacts-tab"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-text-primary">
            Contacts
          </h2>
          <p className="mt-1 max-w-xl text-xs text-text-secondary">
            The people you work with at this Account. Includes their
            contact details and the primary contact for the file.
          </p>
        </div>
        <Button
          variant="primary"
          data-testid="account-contacts-add"
          onClick={onAdd}
        >
          + Add contact
        </Button>
      </header>

      {listQuery.isPending ? (
        <p
          data-testid="account-contacts-loading"
          className="px-3 py-4 text-xs text-text-tertiary"
        >
          Loading…
        </p>
      ) : listQuery.isError ? (
        <p
          data-testid="account-contacts-error"
          className="px-3 py-4 text-xs text-ember-ink"
        >
          Couldn&rsquo;t load Contacts. Try refreshing.
        </p>
      ) : rows.length === 0 ? (
        <p
          data-testid="account-contacts-empty"
          className="rounded-md border border-dashed border-border-subtle px-3 py-6 text-center text-xs text-text-tertiary"
        >
          Add the people you work with at this Account.
        </p>
      ) : (
        <ContactsTable
          rows={rows}
          onEdit={onEdit}
          onMakePrimary={onSetPrimary}
          onRemove={(c) => setRemoving(c)}
          pending={pending}
        />
      )}

      <AccountContactFormModal
        open={modalState !== null}
        mode={modalState?.mode ?? "create"}
        contact={
          modalState?.mode === "edit" ? modalState.contact : null
        }
        hasExistingPrimary={hasExistingPrimary}
        saving={createMutation.isPending || updateMutation.isPending}
        error={modalError}
        onClose={() => setModalState(null)}
        onSubmit={onSubmit}
      />

      <ConfirmDialog
        open={removing !== null}
        title="Remove this contact from the Account?"
        body="The contact will be archived. We keep the row in history; it just stops appearing in the active list."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={onConfirmRemove}
        onCancel={() => setRemoving(null)}
        testId="account-contacts-remove-confirm"
      />
    </section>
  );
}


function ContactsTable({
  rows, onEdit, onMakePrimary, onRemove, pending,
}: {
  rows: AccountContact[];
  onEdit: (c: AccountContact) => void;
  onMakePrimary: (c: AccountContact) => void;
  onRemove: (c: AccountContact) => void;
  pending: boolean;
}) {
  return (
    <Table
      density="xs"
      data-testid="account-contacts-list"
      className="border-collapse"
      wrapperClassName="rounded-md border border-border-subtle"
    >
      <THead className="border-b border-border-subtle bg-canvas/60 text-[11px] uppercase tracking-wide text-text-tertiary">
        <Tr>
          <Th>Name</Th>
          <Th>Job title</Th>
          <Th>Department</Th>
          <Th>Email</Th>
          <Th>Phone</Th>
          <Th>Primary</Th>
          <Th>Actions</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((c) => (
          <ContactRow
            key={c.contact_id}
            contact={c}
            onEdit={() => onEdit(c)}
            onMakePrimary={() => onMakePrimary(c)}
            onRemove={() => onRemove(c)}
            pending={pending}
          />
        ))}
      </TBody>
    </Table>
  );
}


// Thin header-cell wrapper: font-semibold header text; padding + base
// styling come from the design-system cell.
function Th({ children }: { children: React.ReactNode }) {
  return <DsTh className="font-semibold">{children}</DsTh>;
}


function ContactRow({
  contact, onEdit, onMakePrimary, onRemove, pending,
}: {
  contact: AccountContact;
  onEdit: () => void;
  onMakePrimary: () => void;
  onRemove: () => void;
  pending: boolean;
}) {
  const fullName =
    [contact.first_name, contact.last_name].filter(Boolean).join(" ")
    || contact.email
    || "—";
  // Prefer mobile when no desk phone is set so the column always
  // surfaces the most likely-reachable number.
  const phone = contact.phone || contact.mobile_phone || "—";
  return (
    <Tr
      data-testid={`account-contact-row-${contact.contact_id}`}
      className="border-b border-border-subtle/60 last:border-b-0"
    >
      <DsTd className="text-text-primary font-medium">
        {fullName}
      </DsTd>
      <DsTd className="text-text-secondary">
        {contact.job_title || "—"}
      </DsTd>
      <DsTd className="text-text-secondary">
        {contact.department || "—"}
      </DsTd>
      <DsTd className="text-text-secondary">
        {contact.email || "—"}
      </DsTd>
      <DsTd className="text-text-secondary">
        {phone}
      </DsTd>
      <DsTd>
        {contact.is_primary ? (
          <Badge
            variant="category"
            data-testid={`account-contact-primary-badge-${contact.contact_id}`}
          >
            Primary
          </Badge>
        ) : (
          <span className="text-text-tertiary">—</span>
        )}
      </DsTd>
      <DsTd>
        <div className="flex flex-wrap items-center gap-2">
          {!contact.is_primary && (
            <button
              type="button"
              data-testid={`account-contact-make-primary-${contact.contact_id}`}
              onClick={onMakePrimary}
              disabled={pending}
              className="font-medium text-teal hover:underline disabled:opacity-50"
            >
              Make primary
            </button>
          )}
          <button
            type="button"
            data-testid={`account-contact-edit-${contact.contact_id}`}
            onClick={onEdit}
            disabled={pending}
            className="font-medium text-text-secondary hover:underline disabled:opacity-50"
          >
            Edit
          </button>
          <button
            type="button"
            data-testid={`account-contact-remove-${contact.contact_id}`}
            onClick={onRemove}
            disabled={pending}
            className="font-medium text-ember-ink hover:underline disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      </DsTd>
    </Tr>
  );
}


function describeError(err: unknown): string {
  if (err instanceof Error) {
    // Translate the few backend ``detail`` codes we surface to the
    // user. Unknown codes fall through to a friendly default so the
    // banner never reads like a stack trace.
    const msg = err.message;
    if (msg.includes("invalid_email")) return "Enter a valid email address.";
    if (msg.includes("first_name_required")) return "First name is required.";
    if (msg.includes("last_name_required")) return "Last name is required.";
    if (msg.includes("email_required")) return "Email is required.";
    return msg;
  }
  return "Couldn't save the contact. Please try again.";
}
